import { useInView } from '@/lib/useInView';
import { CmsSchema, useCmsFields } from '@/cms/content';
import { CategoryBuyingGuide } from '@/components/CategoryBuyingGuide';
import { REMAINING_SEO_GUIDES } from '@/data/remainingSeoContent';

const BG    = '#ffffff';
const CARD  = '#ffffff';
const GOLD  = '#e05590';
const DARK  = '#2c1a0e';
const MUTED = '#6b5040';

/* ── Content per collection handle ───────────────────────────── */
const SEO_CONTENT: Record<string, { eyebrow: string; blocks: readonly { num: string; h2: string; paragraphs: readonly string[] }[] }> = {
  slub: {
    eyebrow: 'O florystyce ślubnej',
    blocks: [
      {
        num: '01',
        h2: 'Kwiaty ślubne w Lublinie',
        paragraphs: [
          'Ślub to jeden z tych momentów, w których każdy szczegół ma znaczenie. W Bukietowej tworzymy kwiatowe aranżacje, które pomagają nadać uroczystości wyjątkowy charakter. Przygotowujemy bukiety ślubne, dekoracje kwiatowe oraz kompozycje dopasowane do stylu uroczystości, kolorystyki i indywidualnych oczekiwań Pary Młodej. Zadbamy o to, aby kwiaty tworzyły spójną i elegancką oprawę tego wyjątkowego dnia.',
        ],
      },
      {
        num: '02',
        h2: 'Bukiet ślubny dopasowany do Twojego stylu',
        paragraphs: [
          'Bukiet Panny Młodej powinien być nie tylko piękny, ale również dopasowany do sukni, charakteru uroczystości i osobistych upodobań. Tworzymy bukiety ślubne w różnych stylach – od delikatnych i romantycznych kompozycji po bardziej wyraziste i nowoczesne formy. Wspólnie możemy dobrać gatunki kwiatów, kolorystykę oraz kształt bukietu, tak aby całość idealnie pasowała do wizji Waszego ślubu.',
        ],
      },
      {
        num: '03',
        h2: 'Dekoracje kwiatowe na ślub i wesele',
        paragraphs: [
          'Florystyka ślubna to znacznie więcej niż bukiet Panny Młodej. Przygotowujemy również dekoracje kwiatowe na ceremonię i przyjęcie weselne. Kompozycje na stoły, dekoracje sali, kwiaty na samochód czy aranżacje przestrzeni mogą stworzyć spójną oprawę całego wydarzenia. Dopasowujemy charakter dekoracji do wybranego stylu, kolorystyki oraz miejsca, w którym odbywa się uroczystość.',
        ],
      },
      {
        num: '04',
        h2: 'Florystyka ślubna Lublin',
        paragraphs: [
          'Jeśli szukasz florysty na ślub w Lublinie, możemy przygotować dla Ciebie kompleksową oprawę kwiatową tego wyjątkowego dnia. Zależy nam na tym, aby każda realizacja była dopasowana do konkretnej Pary Młodej, dlatego słuchamy potrzeb i pomysłów naszych Klientów. Od pierwszego pomysłu aż po gotowe dekoracje dbamy o estetykę, świeżość kwiatów i spójność wszystkich elementów.',
        ],
      },
    ],
  },
  bukiety: {
    eyebrow: 'O bukietach',
    blocks: [
      {
        num: '01',
        h2: 'Bukiety kwiatów w Lublinie',
        paragraphs: [
          'Bukiet kwiatów to jeden z najprostszych i najpiękniejszych sposobów, aby wyrazić emocje. W Bukietowej tworzymy świeże kompozycje kwiatowe, które mogą być wyjątkowym prezentem na ważną okazję albo spontanicznym gestem bez szczególnego powodu. Nasza kwiaciarnia w Lublinie oferuje bukiety przygotowywane przez florystów z dbałością o dobór kwiatów, kolorów i formę całej kompozycji. Wybierz gotowy bukiet z naszej oferty i spraw komuś przyjemność pięknymi, świeżymi kwiatami.',
        ],
      },
      {
        num: '02',
        h2: 'Bukiety na każdą okazję',
        paragraphs: [
          'Kwiaty doskonale pasują do wielu ważnych momentów. Bukiet możesz podarować z okazji urodzin, imienin, rocznicy, zaręczyn, podziękowania czy gratulacji. Świeże kwiaty są również pięknym sposobem na okazanie miłości, sympatii lub pamięci o bliskiej osobie. W Bukietowej znajdziesz kompozycje dopasowane do różnych okazji i charakteru relacji. Niezależnie od tego, czy szukasz romantycznego bukietu róż, delikatnej kompozycji czy kolorowego bukietu pełnego świeżych kwiatów, możesz wybrać propozycję odpowiednią dla siebie.',
        ],
      },
      {
        num: '03',
        h2: 'Kwiaty z dostawą w Lublinie',
        paragraphs: [
          'Nie zawsze możesz osobiście wręczyć kwiaty osobie, której chcesz sprawić radość. Dlatego Bukietowa oferuje dostawę kwiatów na terenie Lublina i okolic. Zamówienie możesz wygodnie złożyć online, wybrać bukiet, wskazać adres odbiorcy oraz dogodny termin dostawy. Przygotujemy kompozycję i dostarczymy ją pod wskazany adres z należytą starannością. Dzięki temu możesz wysłać kwiaty bliskiej osobie nawet wtedy, gdy dzielą Was kilometry.',
        ],
      },
      {
        num: '04',
        h2: 'Bukietowa – kwiaciarnia internetowa w Lublinie',
        paragraphs: [
          'Bukietowa to lokalna kwiaciarnia, w której łączymy doświadczenie florystów ze swobodą zamawiania kwiatów online. Dbamy o to, aby nasze bukiety były świeże, estetyczne i przygotowane z myślą o osobie, która je otrzyma. Jeśli szukasz bukietu kwiatów w Lublinie z możliwością dostawy, zobacz nasze aktualne kompozycje i wybierz tę, która najlepiej pasuje do okazji. A jeśli nie wiesz, jakie kwiaty wybrać, pozwól, że pomożemy Ci znaleźć odpowiedni bukiet.',
        ],
      },
    ],
  },
};

interface Props {
  handle: string;
}

export function CollectionSeoSection({ handle }: Props) {
  const content = SEO_CONTENT[handle];
  const { ref, inView } = useInView<HTMLElement>('-60px 0px');
  const cms = useCmsFields(content ? [
    { key: 'category.details.eyebrow', label: 'Nadtytuł', section: 'Opis kategorii', kind: 'text', defaultValue: content.eyebrow },
    ...content.blocks.flatMap((block, index) => [
      { key: `category.details.${index}.heading`, label: `Nagłówek ${index + 1}`, section: 'Opis kategorii', kind: 'text' as const, defaultValue: block.h2, required: true },
      ...block.paragraphs.map((paragraph, paragraphIndex) => ({ key: `category.details.${index}.paragraph.${paragraphIndex}`, label: `Akapit ${index + 1}.${paragraphIndex + 1}`, section: 'Opis kategorii', kind: 'textarea' as const, defaultValue: paragraph, required: true })),
    ]),
  ] : []);

  if (REMAINING_SEO_GUIDES[handle]) return <CategoryBuyingGuide content={REMAINING_SEO_GUIDES[handle]} />;
  if (!content) return null;

  const { blocks } = content;

  return (
    <section
      ref={ref}
      aria-label="O kolekcji"
      style={{ background: BG }}
    >
      <CmsSchema fields={cms.fields} />
      <div className="page-container" style={{ paddingBlock: 'clamp(32px, 5vw, 52px)' }}>

        {/* ── Eyebrow ──────────────────────────────────────────── */}
        <div
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, marginBottom: 'clamp(20px, 3vw, 28px)', opacity: inView ? undefined : 0 }}
          className={inView ? 'bk-fade-up' : undefined}
        >
          <span style={{ display: 'block', height: 1, width: 40, background: `linear-gradient(to right, transparent, ${GOLD})` }} />
          <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 9.5, letterSpacing: '0.38em', textTransform: 'uppercase', color: GOLD }}>
            <span {...cms.text('category.details.eyebrow')}>{cms.value('category.details.eyebrow')}</span>
          </span>
          <span style={{ display: 'block', height: 1, width: 40, background: `linear-gradient(to left, transparent, ${GOLD})` }} />
        </div>

        {/* ── Cards ────────────────────────────────────────────── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: 'clamp(16px, 2.5vw, 28px)',
          alignItems: 'stretch',
        }}>
          {blocks.map((block, i) => (
            <article
              key={i}
              style={{
                background: CARD,
                border: `1px solid ${GOLD}28`,
                padding: 'clamp(24px, 3vw, 36px)',
                display: 'flex',
                flexDirection: 'column',
                opacity: inView ? undefined : 0,
                animationDelay: `${0.1 + i * 0.1}s`,
              }}
              className={inView ? 'bk-fade-up' : undefined}
            >
              <p style={{ margin: '0 0 14px', fontFamily: 'DM Sans, sans-serif', fontSize: 10, letterSpacing: '0.32em', textTransform: 'uppercase', color: GOLD }}>
                {block.num}
              </p>

              <h2 style={{ margin: '0 0 16px', fontFamily: '"Playfair Display", Georgia, serif', fontSize: 'clamp(16px, 2vw, 19px)', fontWeight: 600, lineHeight: 1.3, color: DARK, letterSpacing: '-0.01em' }}>
                <span {...cms.text(`category.details.${i}.heading`)}>{cms.value(`category.details.${i}.heading`)}</span>
              </h2>

              <span style={{ display: 'block', width: 32, height: 1, background: `linear-gradient(to right, ${GOLD}, ${GOLD}55)`, marginBottom: 20, flexShrink: 0 }} />

              <div style={{ flex: 1 }}>
                {block.paragraphs.map((p, pi) => (
                  <p key={pi} {...cms.text(`category.details.${i}.paragraph.${pi}`)} style={{ margin: pi > 0 ? '14px 0 0' : 0, fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(13px, 1.4vw, 14.5px)', lineHeight: 1.8, color: MUTED, fontWeight: 400 }}>
                    {cms.value(`category.details.${i}.paragraph.${pi}`)}
                  </p>
                ))}
              </div>
            </article>
          ))}
        </div>

      </div>
    </section>
  );
}
